import _regenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/regenerator.js";
import _asyncToGenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _objectSpread from "/usr/app/node_modules/@babel/runtime/helpers/esm/objectSpread2.js";
import _slicedToArray from "/usr/app/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import React from "react";
var __jsx = React.createElement;
import { useTranslation } from "@i18n";
import { useEffect, useState, useRef, useMemo } from "react";
import { Controller, useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { TextField } from "@/components/ui/text-field";
import { PhoneField, toNationalPhone, toCanonicalPhone } from "@/components/ui/phone-field";
import { SelectField } from "@/components/ui/select-field";
import { Button } from "@/components/ui/button";
import { api__getCitiesDropDown } from "@/apis/assets/api__get_cities_drop_down";
import { CDN_LINK, serverUrl } from "@globalConfig";
import ChangeAccountVerificationModal from "@/_MiniApps/auth/ui/ChangeAccountVerificationModal/ChangeAccountVerificationModal";
import { useAuthActions, useSession } from "@/_MiniApps/auth/AuthContext";
import useApi from "@hooks/useApi";
import Loader from "@componentsShared/Loader/Loader";
import FLowLoadingSpinner from "@/pages/sell-my-car/components/FLowLoadingSpinner";
import RHFCheckbox from "@/common/HOCs/rhf/checkbox/RHFCheckbox";
import { RoadsideAssistanceOption, findRoadServiceSection, getDefaultRoadServiceItem } from "@/_MiniApps/RoadsideAssistance";
import { api__createTamaraCheckoutSession, TamaraValidationError, OrderPaidError, PostReservedError } from "@/apis/tamara/tamara";
import { toast } from "sonner";
import { showTimedSuccessToast } from "@/components/ui/timed-toast";
import { showAppToast } from "@/components/ui/app-toast";
import { trackEvents } from "@/common/events/events";
import useHelpers from "@hooks/useHelpers";
import { ValidationException } from "@/common/exceptions/ValidationException";
import useIsMobile from "@hooks/useIsMobile";
import { getPlatformInfo } from "@/utils/GlobalJavascriptFunction";
import { useTamaraFinance } from "@/common/Contexts/TamaraFinanceContext";
import { getUtmParams } from "@/common/utmParams";
import MobileCarInfoPhotoV2 from "@/pages/checkout/components/HeadingSteps/MobileCarInfo/MobileCarInfoPhotoV2";
import { Icon } from "@syarahtech/ui";
import { useLang } from "@/common/Contexts/LanguageContext";
// Zod Validation Schema
var TamaraFormSchema = z.object({
  first_name: z.string().min(1, "tamara/first_name_required").min(2, "tamara/first_name_min_length").max(50, "tamara/first_name_max_length").regex(/^[\u0600-\u06FFa-zA-Z\s]+$/, "tamara/first_name_invalid_chars"),
  family_name: z.string().min(1, "tamara/family_name_required").min(2, "tamara/family_name_min_length").max(50, "tamara/family_name_max_length").regex(/^[\u0600-\u06FFa-zA-Z\s]+$/, "tamara/family_name_invalid_chars"),
  mobile_number: z.string().min(1, "tamara/mobile_required").regex(/^05\d{8}$/, "flexible_payment/mobile_invalid_format"),
  city_id: z.string().min(1, "tamara/city_required"),
  whatsapp_optin: z.coerce.number()["default"](1)
});
// Helper function to safely extract names from user object
var extractUserNames = function extractUserNames(user) {
  if (!(user !== null && user !== void 0 && user.full_name)) return {
    first_name: "",
    family_name: ""
  };
  var nameParts = user.full_name.trim().split(" ");
  var first_name = nameParts[0] || "";
  var family_name = nameParts.slice(1).join(" ") || "";
  return {
    first_name: first_name,
    family_name: family_name
  };
};
var TamaraInfoComponent = function TamaraInfoComponent(_ref) {
  var _checkoutOrderV2$g4_d, _checkoutOrderV2$sect, _checkoutOrderV2$sect2, _checkoutOrderV2$sect3, _checkoutOrderV2$sect4, _checkoutOrderV2$sect5, _roadServiceSection$s3;
  var setIsOtpStep = _ref.setIsOtpStep,
    autosend = _ref.autosend,
    onFormUpdated = _ref.onFormUpdated,
    onFormCompleted = _ref.onFormCompleted;
  var _useTranslation = useTranslation(["web_checkout_finance"]),
    t = _useTranslation.t,
    tShort = _useTranslation.tShort;
  var _useSession = useSession(),
    user = _useSession.user;
  var _useAuthActions = useAuthActions(),
    logout = _useAuthActions.logout;
  var _useHelpers = useHelpers(),
    navigateTo = _useHelpers.navigateTo;
  var _useTamaraFinance = useTamaraFinance(),
    tamaraActions = _useTamaraFinance.actions,
    tamaraState = _useTamaraFinance.state;
  var checkoutOrderV2 = tamaraState.checkoutOrderV2,
    contextUserInfo = tamaraState.userInfo;
  var fetch__getOrderDetailsByParamsV2 = useTamaraFinance().remote_actions.fetch__getOrderDetailsByParamsV2;
  var isMobile = useIsMobile();
  var lang = useLang();
  var g4DataLayer = checkoutOrderV2 === null || checkoutOrderV2 === void 0 ? void 0 : (_checkoutOrderV2$g4_d = checkoutOrderV2.g4_data) === null || _checkoutOrderV2$g4_d === void 0 ? void 0 : _checkoutOrderV2$g4_d.g4_data_layer;
  var selectedWarrantyId = checkoutOrderV2 === null || checkoutOrderV2 === void 0 ? void 0 : (_checkoutOrderV2$sect = checkoutOrderV2.sections) === null || _checkoutOrderV2$sect === void 0 ? void 0 : (_checkoutOrderV2$sect2 = _checkoutOrderV2$sect.find(function (section) {
    return section.type === "warranty_list";
  })) === null || _checkoutOrderV2$sect2 === void 0 ? void 0 : (_checkoutOrderV2$sect3 = _checkoutOrderV2$sect2.items) === null || _checkoutOrderV2$sect3 === void 0 ? void 0 : (_checkoutOrderV2$sect4 = _checkoutOrderV2$sect3[0]) === null || _checkoutOrderV2$sect4 === void 0 ? void 0 : (_checkoutOrderV2$sect5 = _checkoutOrderV2$sect4.selected) === null || _checkoutOrderV2$sect5 === void 0 ? void 0 : _checkoutOrderV2$sect5.id;
  var _useState = useState(false),
    showVerificationModal = _useState[0],
    setShowVerificationModal = _useState[1];
  var _useApi = useApi([], api__getCitiesDropDown),
    _useApi2 = _slicedToArray(_useApi, 3),
    loadingCities = _useApi2[0],
    error = _useApi2[1],
    cities = _useApi2[2];
  var _useState2 = useState(false),
    isSubmitting = _useState2[0],
    setIsSubmitting = _useState2[1];
  var post_id = useTamaraFinance().queryParams.post_id;

  // Roadside Assistance service (SE-12695). Content + toggle text/state from the API.
  var roadServiceSection = useMemo(function () {
    return findRoadServiceSection(checkoutOrderV2 === null || checkoutOrderV2 === void 0 ? void 0 : checkoutOrderV2.sections);
  }, [checkoutOrderV2]);
  var roadServiceItem = getDefaultRoadServiceItem(roadServiceSection);

  // Memoize city options to prevent unnecessary re-renders
  var cityOptions = useMemo(function () {
    return Array.isArray(cities) ? cities.map(function (city) {
      return {
        value: String(city.id),
        label: city.name
      };
    }) : [];
  }, [cities]);
  // SE-12880: seed from the persisted choice so the toggle survives back-navigation.
  var _useState3 = useState((contextUserInfo === null || contextUserInfo === void 0 ? void 0 : contextUserInfo.road_service_id) != null ? Number(contextUserInfo.road_service_id) !== 0 : true),
    roadsideEnabled = _useState3[0],
    setRoadsideEnabled = _useState3[1];
  useEffect(function () {
    var _ref2, _roadServiceSection$s, _roadServiceSection$s2;
    if (!roadServiceItem) return;
    setRoadsideEnabled((_ref2 = (_roadServiceSection$s = roadServiceSection === null || roadServiceSection === void 0 ? void 0 : (_roadServiceSection$s2 = roadServiceSection.selected_item) === null || _roadServiceSection$s2 === void 0 ? void 0 : _roadServiceSection$s2.toggle) !== null && _roadServiceSection$s !== void 0 ? _roadServiceSection$s : roadServiceItem.selected) !== null && _ref2 !== void 0 ? _ref2 : false);
  }, [roadServiceSection === null || roadServiceSection === void 0 ? void 0 : (_roadServiceSection$s3 = roadServiceSection.selected_item) === null || _roadServiceSection$s3 === void 0 ? void 0 : _roadServiceSection$s3.toggle, roadServiceItem === null || roadServiceItem === void 0 ? void 0 : roadServiceItem.selected]);
  var handleRoadsideToggle = function handleRoadsideToggle(next) {
    var _roadServiceItem$id;
    setRoadsideEnabled(next);
    var rsid = next ? (_roadServiceItem$id = roadServiceItem === null || roadServiceItem === void 0 ? void 0 : roadServiceItem.id) !== null && _roadServiceItem$id !== void 0 ? _roadServiceItem$id : 0 : 0;
    tamaraActions === null || tamaraActions === void 0 ? void 0 : tamaraActions.action__updateUserInfo({
      road_service_id: rsid
    });
    void fetch__getOrderDetailsByParamsV2({
      road_service_id: rsid,
      warranty_id: selectedWarrantyId,
      city_id: cityIdValue
    });
  };

  // Extract user names from session
  var userNames = extractUserNames(user);

  // Initialize default values from context/session/localStorage
  var defaultValues = useMemo(function () {
    var _contextUserInfo$what;
    return {
      first_name: (contextUserInfo === null || contextUserInfo === void 0 ? void 0 : contextUserInfo.first_name) || userNames.first_name || "",
      family_name: (contextUserInfo === null || contextUserInfo === void 0 ? void 0 : contextUserInfo.family_name) || userNames.family_name || "",
      mobile_number: (user === null || user === void 0 ? void 0 : user.phone_number) || (contextUserInfo === null || contextUserInfo === void 0 ? void 0 : contextUserInfo.mobile_number) || "",
      // No default city — the field starts empty so the "اختر مدينتك" placeholder
      // shows and the required-field validation actually bites.
      city_id: contextUserInfo !== null && contextUserInfo !== void 0 && contextUserInfo.city_id ? String(contextUserInfo.city_id) : "",
      whatsapp_optin: Number((_contextUserInfo$what = contextUserInfo === null || contextUserInfo === void 0 ? void 0 : contextUserInfo.whatsapp_optin) !== null && _contextUserInfo$what !== void 0 ? _contextUserInfo$what : 1)
    };
  }, [contextUserInfo, user, userNames]);

  // Initialize RHF
  var methods = useForm({
    resolver: zodResolver(TamaraFormSchema),
    mode: "onTouched",
    defaultValues: defaultValues
  });
  var control = methods.control,
    handleSubmit = methods.handleSubmit,
    watch = methods.watch,
    setValue = methods.setValue,
    setError = methods.setError;

  // Watch specific form values
  var firstNameValue = watch("first_name");
  var familyNameValue = watch("family_name");
  var mobileNumberValue = watch("mobile_number");
  var cityIdValue = watch("city_id");
  var whatsappOptinValue = watch("whatsapp_optin");

  // Track previous city ID for change detection
  var previousCityId = useRef(cityIdValue);

  // Track previous values to avoid unnecessary updates
  var previousValues = useRef({
    first_name: firstNameValue,
    family_name: familyNameValue,
    mobile_number: mobileNumberValue,
    city_id: cityIdValue,
    whatsapp_optin: whatsappOptinValue
  });

  // Sync with localStorage only when values actually change (debounced to prevent loops)
  useEffect(function () {
    var prev = previousValues.current;
    var hasChanged = prev.first_name !== firstNameValue || prev.family_name !== familyNameValue || prev.mobile_number !== mobileNumberValue || prev.city_id !== cityIdValue || prev.whatsapp_optin !== whatsappOptinValue;
    if (hasChanged && tamaraActions !== null && tamaraActions !== void 0 && tamaraActions.action__updateUserInfo) {
      var timer = setTimeout(function () {
        tamaraActions.action__updateUserInfo({
          first_name: firstNameValue || null,
          family_name: familyNameValue || null,
          mobile_number: mobileNumberValue || null,
          city_id: cityIdValue || null,
          whatsapp_optin: whatsappOptinValue
        });
        // Update previous values after syncing
        previousValues.current = {
          first_name: firstNameValue,
          family_name: familyNameValue,
          mobile_number: mobileNumberValue,
          city_id: cityIdValue,
          whatsapp_optin: whatsappOptinValue
        };
      }, 300);
      return function () {
        return clearTimeout(timer);
      };
    }
  }, [firstNameValue, familyNameValue, mobileNumberValue, cityIdValue, whatsappOptinValue, tamaraActions, cities]);

  // City change handler - refresh checkout order
  useEffect(function () {
    if (cityIdValue && previousCityId.current !== undefined && previousCityId.current !== cityIdValue && String(contextUserInfo === null || contextUserInfo === void 0 ? void 0 : contextUserInfo.city_id) !== String(cityIdValue)) {
      // Preserve selected warranty across city re-fetch
      var currentWarrantyId = selectedWarrantyId;
      fetch__getOrderDetailsByParamsV2(_objectSpread({
        city_id: String(cityIdValue)
      }, currentWarrantyId != null && {
        warranty_id: currentWarrantyId
      })).then(function (res) {
        if (res) {
          var msg = t("shipping_cost_updated", {
            ns: "web_checkout_cash"
          });
          showTimedSuccessToast(msg);
        }
      });
    }
    previousCityId.current = cityIdValue;
  }, [cityIdValue, post_id, contextUserInfo === null || contextUserInfo === void 0 ? void 0 : contextUserInfo.city_id]);

  // Auto-send functionality - call onSubmit directly (data was already validated before OTP)
  useEffect(function () {
    if (user !== null && user !== void 0 && user.id && autosend && !isSubmitting) {
      var hasCompleteUserInfo = firstNameValue && familyNameValue && mobileNumberValue && cityIdValue;
      if (hasCompleteUserInfo) {
        onSubmit({
          first_name: firstNameValue,
          family_name: familyNameValue,
          mobile_number: mobileNumberValue,
          city_id: cityIdValue,
          whatsapp_optin: whatsappOptinValue
        });
      }
    }
  }, [autosend]);
  var onSubmit = /*#__PURE__*/function () {
    var _ref3 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee(data) {
      var _response$data, _response$data4, searchParams, experiment_variant, localePrefix, tamaraRequest, response, _response$data2, _response$data3, _checkoutOrderV2$deta, _error$response, apiToFormFieldMap, _errorData$status, _errorData$status2, _errorData$status3, errorData, firstError, _t;
      return _regenerator().w(function (_context) {
        while (1) switch (_context.p = _context.n) {
          case 0:
            // Force update context/local storage with latest form data. This ensures that
            // even if the component unmounts immediately (e.g. switching to OTP step),
            // the latest data is preserved in context and local storage.
            tamaraActions === null || tamaraActions === void 0 ? void 0 : tamaraActions.action__updateUserInfo({
              first_name: data.first_name,
              family_name: data.family_name,
              mobile_number: data.mobile_number,
              city_id: data.city_id,
              whatsapp_optin: data.whatsapp_optin
            });

            // Check if user is logged in first - if not, show OTP step
            if (user !== null && user !== void 0 && user.id) {
              _context.n = 1;
              break;
            }
            onFormCompleted === null || onFormCompleted === void 0 ? void 0 : onFormCompleted();
            setIsOtpStep(true);
            return _context.a(2);
          case 1:
            if (!(!autosend && (data === null || data === void 0 ? void 0 : data.mobile_number) != null && data !== null && data !== void 0 && data.mobile_number && data.mobile_number != (user === null || user === void 0 ? void 0 : user.phone_number) && !showVerificationModal)) {
              _context.n = 2;
              break;
            }
            return _context.a(2, setShowVerificationModal(true));
          case 2:
            if (post_id) {
              _context.n = 3;
              break;
            }
            toast.error(tShort("tamara/car_id_missing"));
            return _context.a(2);
          case 3:
            setIsSubmitting(true);
            _context.p = 4;
            searchParams = new URLSearchParams(__IS_CSR__ ? window.location.search : "");
            experiment_variant = searchParams.get("experiment_variant") || "";
            localePrefix = lang === "en" ? "en/" : ""; // Prepare Tamara checkout request
            tamaraRequest = _objectSpread(_objectSpread(_objectSpread({}, getUtmParams()), {}, {
              post_id: post_id,
              first_name: data.first_name.trim(),
              last_name: data.family_name.trim(),
              phone_number: data.mobile_number,
              city_id: parseInt(data.city_id),
              whatsapp_optin: data.whatsapp_optin,
              success_url: "".concat(serverUrl, "/").concat(localePrefix, "finance-apply-tamara/payment-success?post_id=").concat(post_id, "&experiment_variant=").concat(experiment_variant),
              failure_url: "".concat(serverUrl, "/").concat(localePrefix, "finance-apply-tamara/payment-failed?post_id=").concat(post_id, "&experiment_variant=").concat(experiment_variant),
              cancel_url: "".concat(serverUrl, "/").concat(localePrefix, "finance-apply-tamara/payment-cancel?post_id=").concat(post_id, "&experiment_variant=").concat(experiment_variant)
            }, selectedWarrantyId != null && {
              warranty_id: selectedWarrantyId
            }), roadServiceItem && {
              road_service_id: roadsideEnabled ? roadServiceItem.id : 0
            });
            _context.n = 5;
            return api__createTamaraCheckoutSession(tamaraRequest);
          case 5:
            response = _context.v;
            if (response.success && (_response$data = response.data) !== null && _response$data !== void 0 && _response$data.checkout_url) {
              tamaraActions === null || tamaraActions === void 0 ? void 0 : tamaraActions.action__updateUserInfo({
                first_name: data.first_name,
                family_name: data.family_name,
                mobile_number: data.mobile_number,
                city_id: data.city_id,
                whatsapp_optin: data.whatsapp_optin,
                order_id: String(((_response$data2 = response.data) === null || _response$data2 === void 0 ? void 0 : _response$data2.order_id) || "")
              });
              trackEvents("tamara_form_submit", {
                post_id: post_id,
                order_id: String(((_response$data3 = response.data) === null || _response$data3 === void 0 ? void 0 : _response$data3.order_id) || ""),
                user_city: String(data.city_id),
                inventory_type: g4DataLayer === null || g4DataLayer === void 0 ? void 0 : g4DataLayer.inventory_type,
                post_make: g4DataLayer === null || g4DataLayer === void 0 ? void 0 : g4DataLayer.post_make,
                post_model: g4DataLayer === null || g4DataLayer === void 0 ? void 0 : g4DataLayer.post_model,
                post_trim: g4DataLayer === null || g4DataLayer === void 0 ? void 0 : g4DataLayer.post_ext,
                post_year: g4DataLayer === null || g4DataLayer === void 0 ? void 0 : g4DataLayer.post_year,
                post_condition: g4DataLayer === null || g4DataLayer === void 0 ? void 0 : g4DataLayer.post_condition,
                post_price: g4DataLayer === null || g4DataLayer === void 0 ? void 0 : g4DataLayer.post_price,
                total_amount: checkoutOrderV2 !== null && checkoutOrderV2 !== void 0 && (_checkoutOrderV2$deta = checkoutOrderV2.detail) !== null && _checkoutOrderV2$deta !== void 0 && _checkoutOrderV2$deta.total_amount ? parseFloat(String(checkoutOrderV2.detail.total_amount).replace(/,/g, "")) : undefined,
                platform: getPlatformInfo(),
                referral_location: "pre-checkout",
                experiment_variant: experiment_variant
              });
              onFormCompleted === null || onFormCompleted === void 0 ? void 0 : onFormCompleted();
              // Redirect to Tamara checkout
              window.location.href = response.data.checkout_url;
            } else if ((_response$data4 = response.data) !== null && _response$data4 !== void 0 && _response$data4.rejection_reason) {
              // Checkout session API now performs the pre-scoring check. is_eligible = false
              // is signalled by a rejection_reason → send the user to the rejection screen.
              navigateTo("/finance-apply-tamara/not-eligible?post_id=".concat(post_id));
            } else {
              toast.error(tShort("tamara/payment_session_error"));
            }
            _context.n = 11;
            break;
          case 6:
            _context.p = 6;
            _t = _context.v;
            console.error("Error creating Tamara checkout:", _t);

            // Handle reserve and paid errors first (these take priority)
            if (!(_t instanceof PostReservedError)) {
              _context.n = 7;
              break;
            }
            toast.error(t("car_currently_reserved", {
              ns: "web_checkout_cash"
            }));
            // Redirect to car detail page after a brief delay
            setTimeout(function () {
              navigateTo("/cardetail/".concat(post_id));
            }, 2000);
            return _context.a(2);
          case 7:
            if (!(_t instanceof OrderPaidError)) {
              _context.n = 8;
              break;
            }
            toast.error(t("order_prepaid", {
              ns: "web_checkout_cash"
            }));
            // Redirect to tracking page after a brief delay
            setTimeout(function () {
              navigateTo("/tracking");
            }, 2000);
            return _context.a(2);
          case 8:
            if (!(_t instanceof ValidationException)) {
              _context.n = 9;
              break;
            }
            toast.error(_t.message);
            return _context.a(2);
          case 9:
            if (!(_t instanceof TamaraValidationError)) {
              _context.n = 10;
              break;
            }
            apiToFormFieldMap = {
              first_name: "first_name",
              last_name: "family_name",
              phone_number: "mobile_number",
              city_id: "city_id",
              whatsapp_optin: "whatsapp_optin"
            };
            _t.validationErrors.forEach(function (errorObj) {
              Object.entries(errorObj).forEach(function (_ref4) {
                var _ref5 = _slicedToArray(_ref4, 2),
                  key = _ref5[0],
                  message = _ref5[1];
                var formField = apiToFormFieldMap[key];
                if (formField) {
                  setError(formField, {
                    type: "server",
                    message: message
                  });
                } else {
                  toast.error(message, {
                    position: 'top-center',
                    dismissible: false
                  });
                }
              });
            });
            return _context.a(2);
          case 10:
            // Handle other specific error responses
            if (_t !== null && _t !== void 0 && (_error$response = _t.response) !== null && _error$response !== void 0 && _error$response.data) {
              errorData = _t.response.data;
              if (((_errorData$status = errorData.status) === null || _errorData$status === void 0 ? void 0 : _errorData$status.code) === 1004) {
                toast.error(tShort("tamara/car_not_eligible"), {
                  position: 'top-center',
                  dismissible: false
                });
              } else if (((_errorData$status2 = errorData.status) === null || _errorData$status2 === void 0 ? void 0 : _errorData$status2.code) === 401) {
                toast.error(tShort("tamara/please_login"), {
                  position: 'top-center',
                  dismissible: false
                });
              } else if (errorData.errors) {
                firstError = Object.values(errorData.errors)[0];
                if (typeof firstError === "string") {
                  toast.error(firstError, {
                    position: 'top-center',
                    dismissible: false
                  });
                } else {
                  toast.error(tShort("tamara/check_data"), {
                    position: 'top-center',
                    dismissible: false
                  });
                }
              } else if ((_errorData$status3 = errorData.status) !== null && _errorData$status3 !== void 0 && _errorData$status3.message) {
                toast.error(errorData.status.message, {
                  position: 'top-center',
                  dismissible: false
                });
              } else {
                toast.error(tShort("tamara/unexpected_error"), {
                  position: 'top-center',
                  dismissible: false
                });
              }
            } else {
              toast.error(tShort("tamara/connection_error"), {
                position: 'top-center',
                dismissible: false
              });
            }
          case 11:
            _context.p = 11;
            setIsSubmitting(false);
            return _context.f(11);
          case 12:
            return _context.a(2);
        }
      }, _callee, null, [[4, 6, 11, 12]]);
    }));
    return function onSubmit(_x) {
      return _ref3.apply(this, arguments);
    };
  }();
  var onInvalid = function onInvalid() {
    // SE-13409: the toast is the "you haven't filled anything in" prompt. Once any
    // required field carries a value the inline field errors say what is wrong, so
    // stacking a toast on top of them is noise.
    var allRequiredEmpty = [firstNameValue, familyNameValue, mobileNumberValue, cityIdValue].every(function (value) {
      return !String(value !== null && value !== void 0 ? value : "").trim();
    });
    if (!allRequiredEmpty) return;
    showAppToast(tShort("flexible_payment/toast_fill_fields", {
      ns: "web_checkout_finance"
    }), {
      id: "tamara-fill-fields",
      className: "md:mt-[80px]"
    });
  };
  if (loadingCities) return /*#__PURE__*/React.createElement("div", {
    className: "flex items-center justify-center min-h-[600px]"
  }, /*#__PURE__*/React.createElement(FLowLoadingSpinner, null));
  return /*#__PURE__*/React.createElement(FormProvider, methods, /*#__PURE__*/React.createElement("div", {
    id: "tamara-form-container",
    className: "relative"
  }, /*#__PURE__*/React.createElement("div", {
    id: "tamara-form-inner",
    className: isMobile ? "bg-white border border-solid border-[#e1e8f2] py-5 px-4" : ""
  }, /*#__PURE__*/React.createElement("h1", {
    id: "tamara-form-title",
    className: "py-2 text-base font-bold text-[#00143c] md:py-5 md:text-xl"
  }, tShort("tamara/form_title")), /*#__PURE__*/React.createElement("div", {
    className: "mb-[34px]"
  }, /*#__PURE__*/React.createElement(Controller, {
    name: "first_name",
    control: control,
    render: function render(_ref6) {
      var field = _ref6.field,
        error = _ref6.fieldState.error;
      return /*#__PURE__*/React.createElement(TextField, {
        id: "checkout-firstname-input",
        required: true,
        label: tShort("flexible_payment/first_name_label", {
          ns: "web_checkout_finance"
        }),
        placeholder: tShort("flexible_payment/first_name_placeholder", {
          ns: "web_checkout_finance"
        }),
        value: field.value,
        onChange: function onChange(event) {
          onFormUpdated === null || onFormUpdated === void 0 ? void 0 : onFormUpdated(true);
          field.onChange(event);
        },
        onBlur: field.onBlur,
        error: error ? tShort(error.message, {
          ns: "validations"
        }) : ""
      });
    }
  })), /*#__PURE__*/React.createElement("div", {
    className: "mb-[34px]"
  }, /*#__PURE__*/React.createElement(Controller, {
    name: "family_name",
    control: control,
    render: function render(_ref7) {
      var field = _ref7.field,
        error = _ref7.fieldState.error;
      return /*#__PURE__*/React.createElement(TextField, {
        id: "checkout-lastname-input",
        required: true,
        label: tShort("flexible_payment/family_name_label", {
          ns: "web_checkout_finance"
        }),
        placeholder: tShort("flexible_payment/family_name_placeholder", {
          ns: "web_checkout_finance"
        }),
        value: field.value,
        onChange: function onChange(event) {
          onFormUpdated === null || onFormUpdated === void 0 ? void 0 : onFormUpdated(true);
          field.onChange(event);
        },
        onBlur: field.onBlur,
        error: error ? tShort(error.message, {
          ns: "validations"
        }) : ""
      });
    }
  })), /*#__PURE__*/React.createElement("div", {
    className: "mb-[34px]"
  }, /*#__PURE__*/React.createElement(Controller, {
    name: "mobile_number",
    control: control,
    render: function render(_ref8) {
      var _ref8$field = _ref8.field,
        _onChange = _ref8$field.onChange,
        value = _ref8$field.value,
        onBlur = _ref8$field.onBlur,
        error = _ref8.fieldState.error;
      return /*#__PURE__*/React.createElement(PhoneField, {
        id: "checkout-phone-input",
        required: true,
        maxDigits: 9,
        label: tShort("flexible_payment/phone_label", {
          ns: "web_checkout_finance"
        })
        // Show national (5…) since +966 is displayed; store canonical (05…).
        ,
        value: toNationalPhone(value),
        onChange: function onChange(nextValue) {
          onFormUpdated === null || onFormUpdated === void 0 ? void 0 : onFormUpdated(true);
          _onChange(toCanonicalPhone(nextValue));
        },
        onBlur: onBlur,
        error: error ? tShort(error.message, {
          ns: "validations"
        }) : ""
      });
    }
  }), /*#__PURE__*/React.createElement("label", {
    id: "tamara-whatsapp-label",
    className: "mt-3 flex cursor-pointer items-center gap-1"
  }, /*#__PURE__*/React.createElement("span", {
    onClick: function onClick() {
      return onFormUpdated === null || onFormUpdated === void 0 ? void 0 : onFormUpdated(true);
    }
  }, /*#__PURE__*/React.createElement(RHFCheckbox, {
    name: "whatsapp_optin",
    defaultChecked: true
  })), /*#__PURE__*/React.createElement("img", {
    id: "tamara-whatsapp-icon",
    src: CDN_LINK + "/assets/images/wpImg.svg",
    alt: "whts",
    width: 16,
    height: 16
  }), /*#__PURE__*/React.createElement("span", {
    id: "tamara-whatsapp-text",
    className: "text-xs leading-6 text-[#6c7a8d]"
  }, t("like_receive_updates_order_via", {
    ns: "web_checkout_finance"
  })))), /*#__PURE__*/React.createElement("div", {
    className: isMobile ? "mb-0" : "mb-[34px]"
  }, /*#__PURE__*/React.createElement(Controller, {
    name: "city_id",
    control: control,
    render: function render(_ref9) {
      var field = _ref9.field,
        error = _ref9.fieldState.error;
      return /*#__PURE__*/React.createElement(SelectField, {
        id: "checkout-city-select",
        required: true,
        label: tShort("flexible_payment/city_label", {
          ns: "web_checkout_finance"
        }),
        placeholder: tShort("flexible_payment/city_placeholder", {
          ns: "web_checkout_finance"
        }),
        value: field.value ? String(field.value) : "",
        onChange: function onChange(nextValue) {
          onFormUpdated === null || onFormUpdated === void 0 ? void 0 : onFormUpdated(true);
          field.onChange(nextValue);
        },
        error: error ? tShort(error.message, {
          ns: "validations"
        }) : "",
        options: cityOptions,
        sheetTitle: tShort("flexible_payment/city_sheet_title", {
          ns: "web_checkout_finance"
        }),
        searchPlaceholder: tShort("flexible_payment/city_search_placeholder", {
          ns: "web_checkout_finance"
        })
      });
    }
  }), /*#__PURE__*/React.createElement("p", {
    className: "mt-2 text-[10px] font-semibold text-[#6c7a8d]"
  }, tShort("flexible_payment/city_shipping_note", {
    ns: "web_checkout_finance"
  })))), roadServiceItem && /*#__PURE__*/React.createElement("div", {
    className: isMobile ? "mt-4 bg-white border border-solid border-[#e1e8f2] py-5 px-4" : "mb-[34px]"
  }, /*#__PURE__*/React.createElement(RoadsideAssistanceOption, {
    checked: roadsideEnabled,
    onChange: handleRoadsideToggle,
    item: roadServiceItem
  })), isMobile ? /*#__PURE__*/React.createElement("div", {
    id: "tamara-mobile-submit-wrapper",
    className: "fixed bottom-0 left-0 z-[1] w-full rounded-t-2xl bg-white shadow-[0px_-1px_2px_0px_rgba(0,0,0,0.08)]"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-6 p-4 pb-6"
  }, /*#__PURE__*/React.createElement(MobileCarInfoPhotoV2, {
    checkoutOrderV2: checkoutOrderV2,
    hideData: true
  }), /*#__PURE__*/React.createElement(Button, {
    id: "checkout-submit-btn",
    type: "button",
    className: "relative h-14 w-full gap-3 rounded-md px-3 py-4 text-base font-bold transition-colors hover:bg-primary-120 disabled:bg-[#E8EFF6] disabled:text-[#92A5C1] disabled:opacity-100",
    onClick: handleSubmit(onSubmit, onInvalid),
    disabled: isSubmitting
  }, /*#__PURE__*/React.createElement("strong", null, tShort("flexible_payment/continue", {
    ns: "web_checkout_finance"
  })), lang == "ar" ? /*#__PURE__*/React.createElement(Icon, {
    icon: "material-symbols--arrow-left-alt-rounded",
    width: 6,
    height: 6
  }) : /*#__PURE__*/React.createElement(Icon, {
    icon: "material-symbols--arrow-right-alt-rounded",
    width: 6,
    height: 6
  }), isSubmitting && /*#__PURE__*/React.createElement(Loader, {
    className: "!absolute !inset-0 !w-auto !h-auto !bg-transparent [&>span]:!w-5 [&>span]:!h-5 [&>span]:!border-2 [&>span]:after:!w-5 [&>span]:after:!h-5 [&>span]:after:!border-2"
  })))) :
  /*#__PURE__*/
  /* Submit Button - Desktop: full-width primary CTA (Figma 253:83337) */
  React.createElement("div", {
    id: "tamara-desktop-submit-wrapper",
    className: "mt-8"
  }, /*#__PURE__*/React.createElement(Button, {
    id: "checkout-submit-btn",
    type: "button",
    className: "relative h-14 w-full gap-3 rounded-md px-3 py-4 text-base font-bold transition-colors hover:bg-primary-120 disabled:bg-[#E8EFF6] disabled:text-[#92A5C1] disabled:opacity-100",
    onClick: handleSubmit(onSubmit, onInvalid),
    disabled: isSubmitting
  }, /*#__PURE__*/React.createElement("strong", {
    className: "text-base font-bold leading-6"
  }, tShort("flexible_payment/continue", {
    ns: "web_checkout_finance"
  })), lang == "ar" ? /*#__PURE__*/React.createElement(Icon, {
    icon: "material-symbols--arrow-left-alt-rounded",
    width: 6,
    height: 6
  }) : /*#__PURE__*/React.createElement(Icon, {
    icon: "material-symbols--arrow-right-alt-rounded",
    width: 6,
    height: 6
  }), isSubmitting && /*#__PURE__*/React.createElement(Loader, {
    className: "!absolute !inset-0 !w-auto !h-auto !bg-transparent [&>span]:!w-5 [&>span]:!h-5 [&>span]:!border-2 [&>span]:after:!w-5 [&>span]:after:!h-5 [&>span]:after:!border-2"
  }))), /*#__PURE__*/React.createElement(ChangeAccountVerificationModal, {
    show: showVerificationModal,
    onClose: function onClose() {
      return setShowVerificationModal(false);
    },
    onSubmit: function onSubmit() {
      setShowVerificationModal(false);
      logout(null).then(function () {
        return setIsOtpStep(true);
      });
    },
    newPhoneNumber: mobileNumberValue,
    loading: false
  })));
};
export default TamaraInfoComponent;